import React, { useState, useEffect } from "react";
import { Redirect } from "react-router-dom";
import { API, graphqlOperation } from "aws-amplify";
import NumberFormat from "react-number-format";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import Container from "@material-ui/core/Container";
import Card from "@material-ui/core/Card";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import ListItemText from "@material-ui/core/ListItemText";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import Loading from "../components/Loading";
import ImageLoader from "../components/ImageLoader";
import PaymentButton from "../components/PaymentButton";
import logger from "../logger";
import { CART_STATUS } from "../constants";
import { getPaymentInfo } from "../functions";
import { getCartFull } from "../graphql/queries";
import { updateCart } from "../graphql/mutations";
import { PAYMENT_OPTIONS, DELIVERY_OPTIONS } from "../constants";
import { withContext } from "../context";
import { createTransaction } from "../libs/Payku";

const Payment = ({ history, store, useStoreSlug, cartID }) => {
  const [cart, setCart] = useState(null);
  const [paymentOption, setPaymentOption] = useState(null);
  const [redirect, setRedirect] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const fetchCart = async () => {
      try {
        const vars = {
          id: cartID
        };
        const result = await API.graphql(graphqlOperation(getCartFull, vars));

        if (result.data.getCart) {
          const ticket = JSON.parse(result.data.getCart.ticket);
          const items = JSON.parse(result.data.getCart.items);

          const cart = {
            ...result.data.getCart,
            ticket,
            items
          };

          if (!(cart.status === CART_STATUS.onPayment)) {
            history.push(`${useStoreSlug ? `/${store.slug}` : ""}/comprobante/${cart.id}`);
          }

          setCart(cart);
        } else {
          setRedirect(useStoreSlug ? `/${store.slug}` : "/");
        }
      } catch (err) {
        logger("Payment.fetchingCartFull.error", err.message);
        setRedirect(useStoreSlug ? `/${store.slug}` : "/");
      }
    };

    if (cartID) {
      fetchCart();
    }
  }, [cartID, store, useStoreSlug, history]);

  const sendWhatsapp = message => {
    const url = "https://wa.me/" + store.body.config.whatsapp + "?text=" + message;
    window.open(url, "_blank");
  };

  const saveCart = async params => {
    try {
      const vars = {
        input: {
          id: cartID,
          ...params
        }
      };

      const result = await API.graphql(graphqlOperation(updateCart, vars));

      if (result.data.updateCart) {
        const ticket = JSON.parse(result.data.updateCart.ticket);
        const items = JSON.parse(result.data.updateCart.items);

        const cart = {
          ...result.data.updateCart,
          ticket,
          items
        };

        setCart(cart);

        return cart;
      } else {
        logger(`PaymentWizard.saveCart.result.updateCart`, result);
      }
    } catch (err) {
      logger(`PaymentWizard.saveCart.updateCart.error`, err);
    }

    return null;
  };

  const onCompleted = url => {
    history.push(`${useStoreSlug ? `/${store.slug}` : ""}/${url}`);
  };

  const processPayment = async () => {
    const delivery = {
      type: DELIVERY_OPTIONS.convenience,
      details: JSON.stringify({
        description: "A convenir"
      })
    };

    const payment = {
      type: getPaymentInfo(paymentOption).type
    };

    const paymentDetails = {
      subtotal: cart.items[0].total,
      total: cart.items[0].total,
      deliveryFee: 0
    };

    payment.details = JSON.stringify(paymentDetails);

    let keepLoading = false;

    setSending(true);

    const currentCart = await saveCart({
      delivery,
      payment
    });

    let ticket = null;

    if (typeof currentCart.ticket === "string") {
      ticket = JSON.parse(currentCart.ticket);
    } else {
      ticket = currentCart.ticket;
    }

    if (currentCart) {
      if (paymentOption === PAYMENT_OPTIONS.contraentrega) {
        onCompleted(`comprobante/${cart.id}`);
      } else if (paymentOption === PAYMENT_OPTIONS.transferencia) {
        keepLoading = true;

        setTimeout(() => {
          onCompleted(`transferencia/${currentCart.id}`);
        }, 1500);
      } else {
        const subject = `Compra ${ticket.folio} en ${store.name}`;

        const transaction = await createTransaction({
          order: `${ticket.folio}-${store.code}`,
          subject,
          amount: cart.items[0].total,
          paymentOption: paymentOption,
          marketplaceId: store.body.config.marketplaceId,
          urlReturn: `${window.location.origin}${
            useStoreSlug ? `/${store.slug}` : ""
          }/verificacion/${currentCart.id}`,
          paramsNotify: `cartID=${currentCart.id}`
        });

        if (transaction.id) {
          paymentDetails.transactionID = transaction.id;
          paymentDetails.timestamp = new Date().toISOString();
          paymentDetails.url = transaction.url;

          payment.details = JSON.stringify(paymentDetails);

          const updated = await saveCart({
            status: CART_STATUS.onPayment,
            payment,
            transactionID: transaction.id
          });

          if (updated) {
            if (transaction.url) {
              keepLoading = true;

              setTimeout(() => {
                window.location.href = transaction.url;
              }, 1500);
            }
          } else {
            logger("Payment.processPayment.transaction.cart.error");
          }
        } else {
          logger("Payment.processPayment.transaction.error", transaction);
        }
      }
    } else {
      logger("Payment.processPayment.save.error");
    }

    if (!keepLoading) {
      setSending(false);
    }
  };

  const renderProducts = () => {
    return (
      <List>
        {typeof cart.items === "object" &&
          cart.items.map((item, index) => (
            <React.Fragment key={`item-${index}`}>
              <ListItem disableGutters>
                <ListItemText primary={item.product.name} />
                <ListItemSecondaryAction>
                  {item.quantity === 1 ? (
                    <NumberFormat
                      value={item.total}
                      thousandSeparator="."
                      decimalSeparator=","
                      prefix="$"
                      displayType={"text"}
                    />
                  ) : (
                    <>
                      <NumberFormat
                        value={item.total}
                        thousandSeparator="."
                        decimalSeparator=","
                        prefix="$"
                        displayType={"text"}
                      />
                      {" ("}
                      <NumberFormat
                        value={item.price}
                        thousandSeparator="."
                        decimalSeparator=","
                        prefix="$"
                        displayType={"text"}
                      />
                      {" x "}
                      {item.quantity}
                      {")"}
                    </>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
      </List>
    );
  };

  const renderPayment = () => {
    const hasValidPaymentOptions =
      Boolean(store.body.config.marketplaceId) ||
      (store.body.config.paymentOptions.includes(PAYMENT_OPTIONS.transferencia) &&
        Boolean(store.body.config.recaudacion));

    if (hasValidPaymentOptions) {
      return (
        <div className="payment-options pb-4">
          {store.body.config.paymentOptions.map((option, index) => {
            switch (option) {
              case PAYMENT_OPTIONS.payku_webpay:
                return (
                  <PaymentButton
                    key={`payment-${index}`}
                    option={PAYMENT_OPTIONS.payku_webpay}
                    selected={paymentOption}
                    onClick={() => setPaymentOption(PAYMENT_OPTIONS.payku_webpay)}
                  />
                );
              case PAYMENT_OPTIONS.payku_onepay:
                return (
                  <PaymentButton
                    key={`payment-${index}`}
                    option={PAYMENT_OPTIONS.payku_onepay}
                    selected={paymentOption}
                    onClick={() => setPaymentOption(PAYMENT_OPTIONS.payku_onepay)}
                  />
                );
              case PAYMENT_OPTIONS.contraentrega:
                return (
                  <PaymentButton
                    key={`payment-${index}`}
                    option={PAYMENT_OPTIONS.contraentrega}
                    selected={paymentOption}
                    onClick={() => setPaymentOption(PAYMENT_OPTIONS.contraentrega)}
                  />
                );
              case PAYMENT_OPTIONS.payku_khipu:
                return (
                  <PaymentButton
                    key={`payment-${index}`}
                    option={PAYMENT_OPTIONS.payku_khipu}
                    selected={paymentOption}
                    onClick={() => setPaymentOption(PAYMENT_OPTIONS.payku_khipu)}
                  />
                );
              case PAYMENT_OPTIONS.transferencia:
                return (
                  <PaymentButton
                    key={`payment-${index}`}
                    option={PAYMENT_OPTIONS.transferencia}
                    selected={paymentOption}
                    onClick={() => setPaymentOption(PAYMENT_OPTIONS.transferencia)}
                  />
                );
              default:
                return null;
            }
          })}
        </div>
      );
    } else {
      return (
        <Typography className="mt-4" variant="h6" align="center" color="textSecondary">
          No hay medios de pagos habilitados
        </Typography>
      );
    }
  };

  if (!cart || !store) {
    return (
      <>
        <Loading fullscreen />
        {redirect && <Redirect to={redirect} />}
      </>
    );
  }

  return (
    <>
      {cart ? (
        <>
          <AppBar color="default" position="sticky" className="app-top-bar">
            <Toolbar>
              {store.logo ? (
                <ImageLoader imgKey={store.logo.key} className="logo" circularProgressSize={20} />
              ) : (
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/avatar.png`}
                  className="logo"
                  alt="Logo"
                />
              )}
              <Typography variant="h6" className="title">
                {store.name}
              </Typography>
              {store.body.config && store.body.config.whatsapp && (
                <IconButton
                  aria-label="Conversar con ejecutivo"
                  onClick={() => sendWhatsapp("Hola, quiero consultar sobre un pago.")}
                >
                  <WhatsAppIcon />
                </IconButton>
              )}
            </Toolbar>
          </AppBar>

          <Container maxWidth="xs" className="mt-4 mb-8">
            <Typography variant="h5" align="left">
              {`${cart.client.firstname}, Elige la forma de pago`}
            </Typography>

            <Typography variant="body1" className="mt-3">
              {`Número de pedido ${cart.ticket.folio}`}
            </Typography>

            <Card className="mt-2 summary-products" elevation={0}>
              {renderProducts()}
            </Card>

            {renderPayment()}

            {sending && (
              <div className="mb-4">
                <Loading circularProgressSize={25} />
              </div>
            )}

            <Button
              fullWidth
              className="mb-5 wide"
              variant="contained"
              size="large"
              disabled={!paymentOption || sending}
              onClick={processPayment}
            >
              <NumberFormat
                value={cart.items[0].total}
                thousandSeparator="."
                decimalSeparator=","
                prefix="Realizar pago $"
                displayType={"text"}
              />
            </Button>
          </Container>
        </>
      ) : (
        <Loading />
      )}
    </>
  );
};

export default withContext(Payment);
